/**
 * Client-side rate limiter for create prospect endpoint
 * Prevents rapid-fire signup attempts from the same browser
 */

import { formatTimeOfDay } from '@/utils/time-format';

interface RateLimitEntry {
  timestamp: number;
  count: number;
}

const STORAGE_KEY = 'lexi_signup_ratelimit';
const MAX_ATTEMPTS = 5; // Maximum attempts allowed
const TIME_WINDOW_MS = 60 * 60 * 1000; // 1 hour window

/**
 * Get rate limit data from localStorage
 */
function getRateLimitData(): RateLimitEntry {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      return { timestamp: Date.now(), count: 0 };
    }
    return JSON.parse(data);
  } catch {
    return { timestamp: Date.now(), count: 0 };
  }
}

/**
 * Save rate limit data to localStorage
 */
function setRateLimitData(data: RateLimitEntry): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Fail silently if localStorage is not available
  }
}

/**
 * Check if the user has exceeded rate limit
 * Returns { allowed: boolean, remainingAttempts: number, resetTime: Date }
 */
export function checkCreateProspectRateLimit(): {
  allowed: boolean;
  remainingAttempts: number;
  resetTime: Date;
  message?: string;
} {
  const rateLimitData = getRateLimitData();
  const now = Date.now();
  const timePassed = now - rateLimitData.timestamp;

  // Reset if time window has passed
  if (timePassed > TIME_WINDOW_MS) {
    setRateLimitData({ timestamp: now, count: 1 });
    return {
      allowed: true,
      remainingAttempts: MAX_ATTEMPTS - 1,
      resetTime: new Date(now + TIME_WINDOW_MS),
    };
  }

  // Check if under limit
  if (rateLimitData.count < MAX_ATTEMPTS) {
    rateLimitData.count += 1;
    setRateLimitData(rateLimitData);
    return {
      allowed: true,
      remainingAttempts: MAX_ATTEMPTS - rateLimitData.count,
      resetTime: new Date(rateLimitData.timestamp + TIME_WINDOW_MS),
    };
  }

  // Rate limit exceeded
  const resetTime = new Date(rateLimitData.timestamp + TIME_WINDOW_MS);
  return {
    allowed: false,
    remainingAttempts: 0,
    resetTime,
    message: `Too many signup attempts. Please try again after ${formatTimeOfDay(resetTime, { withZone: true })}`,
  };
}

/**
 * Reset rate limit (for testing or manual reset)
 */
export function resetCreateProspectRateLimit(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Fail silently
  }
}
